import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { deleteCampaign, getAllCampaigns } from '../utils/api/campaignAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import AddCampaignModal from './child/AddCampaignModal';
import ViewCampaignModal from './child/ViewCampaignModal';
import EditCampaignModal from './child/EditCampaignModal';
import ConfirmDeleteModal from './child/ConfirmDeleteModal';

function CampaignLayer() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') || '', 10) || 1;
  const limit = parseInt(searchParams.get('limit') || '', 10) || 10;
  const orderBy = searchParams.get('orderBy') || 'createdAt';
  const orderDirection = searchParams.get('orderDirection') || 'DESC';

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [isLocked, setIsLocked] = useState(true);

  const updateURLParams = (newParams) => {
    setSearchParams({
      page: (newParams.page ?? page).toString(),
      limit: (newParams.limit ?? limit).toString(),
      orderBy: newParams.orderBy ?? orderBy,
      orderDirection: newParams.orderDirection ?? orderDirection,
    });
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const payload = {
        page,
        limit,
        orderBy,
        orderDirection,
      };
      const res = await getAllCampaigns(payload);
      
      // Handle response structure: { success, message, campaigns, pagination }
      if (res?.success && res?.campaigns) {
        setCampaigns(Array.isArray(res.campaigns) ? res.campaigns : []);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 0);
          setTotalItems(res.pagination.total || 0);
        }
      } else {
        setCampaigns([]);
        setTotalPages(0);
        setTotalItems(0);
      }
    } catch (e) {
      console.error('Error fetching campaigns:', e);
      toast.error(e?.message || 'Failed to load campaigns');
      setCampaigns([]);
      setTotalPages(0);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [page, limit, orderBy, orderDirection]);

  const handleCampaignCreated = () => {
    fetchCampaigns();
  };

  const handleDelete = (id) => {
    const campaign = campaigns.find(c => (c.id || c._id) === id);
    setCampaignToDelete({ id, name: campaign?.name || 'this campaign' });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!campaignToDelete) return;
    
    try {
      const res = await deleteCampaign(campaignToDelete.id);
      toast.success(res?.message || 'Campaign deleted successfully');
      setShowDeleteModal(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error?.message || 'Error deleting campaign');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCampaignToDelete(null);
  };

  const handleSortChange = (field) => {
    if (orderBy === field) {
      updateURLParams({ orderDirection: orderDirection === 'ASC' ? 'DESC' : 'ASC', page: 1 });
    } else {
      updateURLParams({ orderBy: field, orderDirection: 'DESC', page: 1 });
    }
  };

  const getSortIcon = (field) => {
    if (orderBy !== field) {
      return <Icon icon="lucide:arrow-up-down" width="14" height="14" />;
    }
    return orderDirection === 'ASC' 
      ? <Icon icon="lucide:arrow-up" width="14" height="14" />
      : <Icon icon="lucide:arrow-down" width="14" height="14" />;
  };

  return (
    <div style={{ zIndex: 1000, color: '' }}>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="card h-100 p-0 radius-12"
      style={{ position: 'relative' }}
    >
      <div className="card-header border-bottom bg-base py-16 px-24">
        <div className="d-flex justify-content-between align-items-center flex-wrap w-100" style={{ gap: '12px' }}>
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="order-by-filter" className="mb-0">Order By:</label>
            <div className="position-relative" style={{ width: '150px' }}>
              <Icon
                icon="lucide:arrow-up-down"
                width="18"
                height="18"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
              <select
                id="order-by-filter"
                name="orderBy"
                className="form-control"
                value={orderBy}
                onChange={(e) => updateURLParams({ orderBy: e.target.value, page: 1 })}
                style={{ paddingLeft: '36px', paddingRight: '30px', height: '45px', borderRadius: '4px' }}
              >
                <option value="createdAt">Created At</option>
                <option value="updatedAt">Updated At</option>
                <option value="name">Name</option>
              </select>
              <Icon
                icon="lucide:chevron-down"
                width="18"
                height="18"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
            </div>
            <div className="position-relative" style={{ width: '120px' }}>
              <Icon
                icon="lucide:arrow-up-down"
                width="18"
                height="18"
                style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
              <select
                name="orderDirection"
                className="form-control"
                value={orderDirection}
                onChange={(e) => updateURLParams({ orderDirection: e.target.value, page: 1 })}
                style={{ paddingLeft: '36px', paddingRight: '30px', height: '45px', borderRadius: '4px' }}
              >
                <option value="ASC">ASC</option>
                <option value="DESC">DESC</option>
              </select>
              <Icon
                icon="lucide:chevron-down"
                width="18"
                height="18"
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#6b7280' }}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            + Add New Campaign
          </button>
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th 
                  className="text-center" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('name')}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    Campaign Name
                    {getSortIcon('name')}
                  </div>
                </th>
                <th className="text-center">Assistant</th>
                <th className="text-center">Phone Number</th>
                <th className="text-center">Customers Count</th>
                <th 
                  className="text-center" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('createdAt')}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    Created At
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th 
                  className="text-center" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('updatedAt')}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    Updated At
                    {getSortIcon('updatedAt')}
                  </div>
                </th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`shimmer-${idx}`}>
                    {Array.from({ length: 7 }).map((_, i) => (
                      <td key={i}><div className="shimmer shimmer-text" /></td>
                    ))}
                  </tr>
                ))
              ) : campaigns.length > 0 ? (
                campaigns.map((item) => {
                  const rowId = item?.id || item?._id || `${item.name}-${item.createdAt}`;
                  return (
                    <tr key={rowId}>
                      <td className="text-center">{item?.name || '-'}</td>
                      <td className="text-center">
                        {item?.assistant?.name || item?.assistantId || '-'}
                      </td>
                      <td className="text-center">
                        {item?.phoneNumber?.did || item?.phoneNumberId || '-'}
                      </td>
                      <td className="text-center">
                        {Array.isArray(item?.customers) ? item.customers.length : 0}
                      </td>
                      <td className="text-center">
                        {item?.createdAt ? convertToIndianTimeZone(item.createdAt) : '-'}
                      </td>
                      <td className="text-center">
                        {item?.updatedAt ? convertToIndianTimeZone(item.updatedAt) : '-'}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-10 justify-content-center">
                          <button
                            onClick={() => {
                              setSelectedCampaignId(item?.id || item?._id);
                              setShowViewModal(true);
                            }}
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="majesticons:eye-line" className="icon text-xl" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCampaignId(item?.id || item?._id);
                              setShowEditModal(true);
                            }}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                            onClick={() => handleDelete(item?.id || item?._id)}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="ic:outline-block" className="menu-icon" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div
                      className="d-flex flex-column justify-content-center align-items-center"
                      style={{ height: '40vh' }}
                    >
                      <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                      <p className="text-secondary-light text-md mb-0">No campaigns found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && campaigns.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-10">
            <div className="text-muted">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalItems)} of {totalItems} campaigns
            </div>
            <div className="d-flex gap-2 ms-auto" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => updateURLParams({ page: Math.max(page - 1, 1) })}
                disabled={page === 1}
              >
                Prev
              </button>
              <span className="d-flex align-items-center px-3">
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => updateURLParams({ page: Math.min(page + 1, totalPages) })}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
       {/* ===================== 🔒 LOCK OVERLAY ===================== */}
       {isLocked && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'all',
              borderRadius: '12px',
            }}
          >
            <div className="text-center">
              <Icon icon="lucide:lock" width="56" height="56" className="text-secondary mb-3" />
              <p className="text-muted fw-medium mb-0">
                Contact us to unlock access
              </p>
            </div>
          </div>
        )}

      {/* Modals */}
      <AddCampaignModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleCampaignCreated}
      />
      <ViewCampaignModal
        id={selectedCampaignId}
        show={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedCampaignId(null);
        }}
      />
      <EditCampaignModal
        id={selectedCampaignId}
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCampaignId(null);
        }}
        onSuccess={handleCampaignCreated}
      />
      {showDeleteModal && campaignToDelete && (
        <ConfirmDeleteModal
          name={campaignToDelete.name}
          isCampaign={true}
          handleConfirmDelete={handleConfirmDelete}
          handleCancelDelete={handleCancelDelete}
        />
      )}
    </motion.div>
    </div>
  );
}

export default CampaignLayer;

